import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/3f757860/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`General guidance`}</h2>
    <p>{`Tiles are a highly flexible component for displaying a wide variety of content, including informational, getting started, how-to, next steps, and more.`}</p>
    <p>{`Carbon ships a basic tile structure that responds to the grid. Tiles have no pre-set styles for the content within them. You can customize tiles to fit your specific use case.`}</p>
    <p>{`When using a call-to-action (CTA) within a tile, use a `}<a parentName="p" {...{
        "href": "/components/button/code"
      }}>{`secondary button`}</a>{`. Primary buttons should be reserved for the most important action a user can take on the page.`}</p>
    <h2>{`Variations`}</h2>
    <p><em parentName="p">{`All of the images below represent examples of types of content that could be presented within a tile. The styling in these examples is for illustration only; you are free to create your own layout and design within a tile.`}</em></p>
    <h3>{`Read-only`}</h3>
    <p>{`Read-only tiles are used to display information to the user, such as features or services offered. Read-only tiles are often seen on marketing pages to promote content. These tiles can have internal calls-to-action (CTAs), such as a `}<a parentName="p" {...{
        "href": "/components/button/code"
      }}>{`button`}</a>{` or a `}<a parentName="p" {...{
        "href": "/components/link/code"
      }}>{`link`}</a>{`.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "42.76785714285714%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAACEUlEQVQoz2NggAKtkgMM5ID///+jCvCZHWLQLj7PCGJLBZ3wkfXY0KyRerJSMWJ3tUrormpR371VeW0HohcvmVtd1djWEJ9VWl9QVlnbM3FS2679B0JA+s6cO8uwcv0GiIGK0acZ5JIuMYHYCokXVqnH3fuvlnvvv2rclf/q8df/S8dc/18788LxpUvn/I+IT/uvoGH6397d7//6LVv+X7x6dTtI35u3rxh3HzwIMVAq5SKDRuZVsIGKGVfnyKVe/q+YceqbXPzxX0LBJ34JArGed+c2C3uXj5rG9n8Y+DR+6TtFfo9IL/3vGZGxDKQvq7KD0SkwEWJgz56vDO2rzoIN7NryeP6kbc/+T9h6/3fPmrv/+tc++N++6M6/mq4dO0vqO7+VVTX975qy4F9D+8TfTb2z/7dNWb4KpK9j+irGpv4F0ED9/Ynh4pnjYAPfPn8w583zB/9fPLn37f3rJ7/+/Pz06///7z////ux7cnjhx9fv3755/27N79+//r5/T8ELINGDCM8cj58/MTw8MkLsIEvX79bDVL17fuP/8jg3bsPJ959+PT/1+8////9+w/E/8DiHz58AIfhhw8fGd9/+AAx8NOnzwx3n38Hx/LLd1/9nrz+1vrl67fqL18+137+DMKfaj98/Bz96NW3yvefvtV9/foFJF7z6dOnFiAOhpjxCYxRwP1Xv8hKh+gGAQCBKEjQOO66NAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example image of read-only tiles.",
            "title": "Example image of read-only tiles.",
            "src": "/static/449e0e5564276a269e55602b1e9447e7/3cbba/tile-usage-1.png",
            "srcSet": ["/static/449e0e5564276a269e55602b1e9447e7/7fc1e/tile-usage-1.png 288w", "/static/449e0e5564276a269e55602b1e9447e7/a5df1/tile-usage-1.png 576w", "/static/449e0e5564276a269e55602b1e9447e7/3cbba/tile-usage-1.png 1152w", "/static/449e0e5564276a269e55602b1e9447e7/0b124/tile-usage-1.png 1728w", "/static/449e0e5564276a269e55602b1e9447e7/99571/tile-usage-1.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Clickable`}</h3>
    <p>{`Clickable tiles can be used as navigational items, where the entire tile is a clickable state, which redirects the user to a new page. Clickable tiles cannot contain separate internal CTAs.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "28.035714285714285%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAYAAADDl76dAAAACXBIWXMAAAsSAAALEgHS3X78AAAA20lEQVQY052RSw6CQAyGITFuPRR34BYsdCGIuNA7GI9jvAZbwivhPSCPGOpfAhjDjibNzPxtv7YZSRotiiJZWmlFUfweQogZliSJEobh1XXdI5IMxPSyLK08z9UgCPbQTeSybkA/49QQ206cGRjH8YbvCN4JZts2+b5Pnufxk1D8RKMPgOQ4DvFZ1zXrIk3T3QIIH4BVVT0YgCkIiYQGE/CFHGqaZtDZ+76nLMveXdctgVhPHidUUHxr21aHnwDg1S6Iq/ADpjJZh/PaFjbT0O9/5ckAW/0pgM73LwJZ8CrJhl+JAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example image of clickable tiles.",
            "title": "Example image of clickable tiles.",
            "src": "/static/ab3d5f3baa572c1136b00f317f67a4ea/3cbba/tile-usage-2.png",
            "srcSet": ["/static/ab3d5f3baa572c1136b00f317f67a4ea/7fc1e/tile-usage-2.png 288w", "/static/ab3d5f3baa572c1136b00f317f67a4ea/a5df1/tile-usage-2.png 576w", "/static/ab3d5f3baa572c1136b00f317f67a4ea/3cbba/tile-usage-2.png 1152w", "/static/ab3d5f3baa572c1136b00f317f67a4ea/0b124/tile-usage-2.png 1728w", "/static/ab3d5f3baa572c1136b00f317f67a4ea/99571/tile-usage-2.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Selectable`}</h3>
    <p>{`Selectable tiles work like a `}<a parentName="p" {...{
        "href": "/components/radio-button/code"
      }}>{`radio button`}</a>{`, where the entire tile is a click target. Selectable tiles may contain internal CTAs (like links to docs) if the internal CTA is given its own click target. Selectable tiles work well for presenting options to a user in a structured manner, such as a set of pricing plans.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "28.035714285714285%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAYAAADDl76dAAAACXBIWXMAAAsSAAALEgHS3X78AAAA5UlEQVQY061PS4rCUBB89/AqzjkEN7ryBk5eJHg4mYW4l0kgZJX//x8SkrTVYUZ0b0PxuvtVdXUL8enwPG9r2/a5KAolz3NZVZVa17WC/kbX9aPjOKrv+zKOY+m6rmYYxg5/B87LspTQLRrkCt6VsCzrmiQJQURpmtI4jjTPM9cXiCkIAoqiiGBCHKjvpmnaYRgSI8uyhc/A0LXAsBsTu66j18CQHxBoGAaapunZh+kvtnU57/v+TQP+l2iaZtu27RngldU/KHDfQPiNDU4wVWEgAQ2n7rHtAXwNWj73/2S5nPzpeADCGhfXjQ5jxgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example image of selectable tiles.",
            "title": "Example image of selectable tiles.",
            "src": "/static/941b36e937ad7a87ef69b988c64e9672/3cbba/tile-usage-3.png",
            "srcSet": ["/static/941b36e937ad7a87ef69b988c64e9672/7fc1e/tile-usage-3.png 288w", "/static/941b36e937ad7a87ef69b988c64e9672/a5df1/tile-usage-3.png 576w", "/static/941b36e937ad7a87ef69b988c64e9672/3cbba/tile-usage-3.png 1152w", "/static/941b36e937ad7a87ef69b988c64e9672/0b124/tile-usage-3.png 1728w", "/static/941b36e937ad7a87ef69b988c64e9672/99571/tile-usage-3.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Expandable`}</h3>
    <p>{`Expandable tiles are helpful for hiding/showing larger amounts of content to a user. They can only be stacked in a single column, and cannot live in a row or horizontal grid. When expanded, tiles push content down the page. Expandable tiles may contain internal CTAs (like links to docs) if the internal CTA is given its own click target.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "57.14285714285714%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAABnUlEQVQoz6WS2UrDUBCGk97mBcQLUQTBivisSvoqhfaiEH2JtpTuW5LuW2IWj98cktgKXhn488/Myfyz5Bjr9do0eDabzbXv+2/tdrvSbDbtTqdjt1ot23Vdm2/s3W5n7/f7gjO7Ar+CK9GATWO73ZbE4fB5tVp9DgYDBdLZbJYili4WCw3OUxI058BXcIjGY6ZhSme6Q4J3y+Wyili91+vVBNiaEayRXCOhgPigjl0FN+eCeYcvUpHxLiCxOI5VmqYqSZICeSwIAvnmqRiZVylznkHY7/fT6XQaUyjBTkajkWYBa0g8z0tYhXBMQRk7oJmfkXkVHR4OBzWfz9VwOFSsQE0mE22Px2MNxBXFFLtWp9NJhWEo3X2B8nmHZuaUEZwi6FPdxfaOx6NHXIOCHkU0spgLfOITYg/ZfzCN/Nrg3LP8Bj/ivdvtOoyogbjD+A6JDgIaYmf+B9wAt8XIv65NIHtirAiOZZfsK5Z9cR4joDlDhJ/Ap4trE0WR2AY7KSmlrL/ACiwSLEQ0cpurZlFUT4lt5GLGfx+m0PwN+ahwA9dwR2sAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example image of expandable tiles.",
            "title": "Example image of expandable tiles.",
            "src": "/static/42880cf5b47cb4ad762d0e62491acc28/3cbba/tile-usage-4.png",
            "srcSet": ["/static/42880cf5b47cb4ad762d0e62491acc28/7fc1e/tile-usage-4.png 288w", "/static/42880cf5b47cb4ad762d0e62491acc28/a5df1/tile-usage-4.png 576w", "/static/42880cf5b47cb4ad762d0e62491acc28/3cbba/tile-usage-4.png 1152w", "/static/42880cf5b47cb4ad762d0e62491acc28/0b124/tile-usage-4.png 1728w", "/static/42880cf5b47cb4ad762d0e62491acc28/99571/tile-usage-4.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      